/** AppleCare Requests — internal Apple Services console.
 *  Operations → Apple Services → AppleCare Requests.
 *  Tabs: Overview · Requests · Customer Links · Plan Catalogue ·
 *  Enterprise Agreements · Data Quality. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/app/StatusBadge";
import {
  ShieldCheck, Link2, FileText, Building2, AlertTriangle, LayoutDashboard,
  Plus, Send, Ban, RefreshCw, Copy, Upload, Download, Loader2, TrendingUp, Settings2,
} from "lucide-react";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["requests", "Requests", ShieldCheck],
  ["upsell", "Upsell Opportunities", TrendingUp],
  ["links", "Customer Links", Link2],
  ["plans", "Plan Catalogue", FileText],
  ["agreements", "Enterprise Agreements", Building2],
  ["dq", "Data Quality", AlertTriangle],
  ["channels", "Channels & Verify", Send],
  ["upsellcfg", "Upsell Config", Settings2],
];

const money = (a, c = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: c || "INR", maximumFractionDigits: 0 }).format(a || 0);

export default function AppleCareRequests() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="max-w-[1200px] mx-auto px-4 py-5" data-testid="applecare-admin">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck className="h-6 w-6 text-emerald-600" />
        <h1 className="text-2xl font-bold text-slate-900">AppleCare Requests</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Customer self-service AppleCare — links, device verification, plan eligibility and enterprise gating.</p>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-5">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ac-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview go={setTab} />}
      {tab === "requests" && <Requests />}
      {tab === "upsell" && <Upsell />}
      {tab === "links" && <Links />}
      {tab === "plans" && <Plans />}
      {tab === "agreements" && <Agreements />}
      {tab === "dq" && <DataQuality />}
      {tab === "channels" && <ChannelsVerify />}
      {tab === "upsellcfg" && <UpsellConfig />}
    </div>
  );
}

/* ------------------------------ Overview ------------------------------ */
function Overview({ go }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/applecare/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Skeleton />;
  const tiles = [
    ["Requests", d.requests_total, "requests"],
    ["Active links", d.active_links, "links"],
    ["Active plans", d.plans_active, "plans"],
    ["Agreements", d.agreements_active, "agreements"],
    ["Data quality", d.data_quality_issues, "dq"],
  ];
  return (
    <div className="space-y-5" data-testid="ac-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map(([label, val, t]) => (
          <button key={label} onClick={() => go(t)} className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-left hover:border-emerald-300">
            <div className="text-2xl font-bold text-slate-900">{val ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
          </button>
        ))}
      </div>
      <Panel title="Recent requests">
        {(d.recent_requests || []).length === 0 ? <Empty text="No requests yet" /> : (
          <Table head={["Request", "Device", "Plan", "Status"]}>
            {d.recent_requests.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <Td mono>{r.acr_id}</Td>
                <Td>{r.device_model || r.device_family || "—"}</Td>
                <Td>{r.selected_plan?.description || "—"}</Td>
                <Td><StatusBadge value={r.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------ Requests ------------------------------ */
function Requests() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/applecare/requests").then(({ data }) => setRows(data.items)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Skeleton />;
  return (
    <Panel title={`Requests (${rows.length})`}>
      {rows.length === 0 ? <Empty text="No AppleCare requests submitted yet" /> : (
        <Table head={["Request", "Contact", "Company", "Device", "Plan", "Enterprise", "Status"]} testId="ac-requests-table">
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`ac-request-${r.id}`}>
              <Td mono>{r.acr_id}</Td>
              <Td>{r.contact_name}<div className="text-[11px] text-slate-400">{r.contact_email}</div></Td>
              <Td>{r.company_name || "—"}</Td>
              <Td>{r.device_model || r.device_family || "—"}<div className="text-[11px] text-slate-400">{r.serial_masked}</div></Td>
              <Td>{r.selected_plan ? <>{r.selected_plan.description}<div className="text-[11px] text-slate-400">{money(r.selected_plan.amount, r.selected_plan.currency)}</div></> : "—"}</Td>
              <Td>{r.is_enterprise ? (r.agreement_masked || "Yes") : "No"}</Td>
              <Td><StatusBadge value={r.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

/* ------------------------------ Links ------------------------------ */
function Links() {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ customer_name: "", contact_name: "", contact_email: "", contact_mobile: "", market: "IN", max_submissions: 1, channel: "EMAIL" });
  const load = useCallback(() => api.get("/applecare/links").then(({ data }) => setRows(data.items)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/applecare/links", { ...form, max_submissions: Number(form.max_submissions) });
      toast.success("AppleCare link created");
      setCreating(false);
      setForm({ customer_name: "", contact_name: "", contact_email: "", market: "IN", max_submissions: 1, channel: "EMAIL" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (id, fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copied"); };

  if (!rows) return <Skeleton />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating((v) => !v)} data-testid="ac-create-link-btn" className="bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" /> Create AppleCare link</Button>
      </div>
      {creating && (
        <Panel title="New customer link">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ac-link-form">
            <L label="Customer / company"><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ac-link-customer" /></L>
            <L label="Account manager / market"><Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} data-testid="ac-link-market" /></L>
            <L label="Contact name"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="ac-link-contact" /></L>
            <L label="Contact email"><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="ac-link-email" /></L>
            <L label="Contact mobile (for SMS / WhatsApp)"><Input value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} placeholder="+91..." data-testid="ac-link-mobile" /></L>
            <L label="Allowed submissions"><Input type="number" min="1" value={form.max_submissions} onChange={(e) => setForm({ ...form, max_submissions: e.target.value })} data-testid="ac-link-max" /></L>
            <L label="Channel">
              <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="ac-link-channel" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
                <option value="EMAIL">Email</option>
                <option value="SMS">SMS (setup required)</option>
                <option value="WHATSAPP">WhatsApp (setup required)</option>
              </select>
            </L>
          </div>
          <div className="flex justify-end mt-3"><Button onClick={create} data-testid="ac-link-save" className="bg-emerald-700 hover:bg-emerald-800">Create link</Button></div>
        </Panel>
      )}
      <Panel title={`Customer links (${rows.length})`}>
        {rows.length === 0 ? <Empty text="No links yet" /> : (
          <Table head={["Request ID", "Customer", "Contact", "Uses", "Status", "Actions"]} testId="ac-links-table">
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-100" data-testid={`ac-link-${l.id}`}>
                <Td mono>{l.acr_id}</Td>
                <Td>{l.customer_name || "—"}</Td>
                <Td>{l.contact_email || "—"}</Td>
                <Td>{l.submissions_used || 0}/{l.max_submissions}</Td>
                <Td><StatusBadge value={l.status} /></Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <IconBtn title="Copy link" onClick={() => copy(l.link_url)} testId={`ac-link-copy-${l.id}`}><Copy className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title={`Send via ${l.channel || "EMAIL"}`} onClick={() => act(l.id, () => api.post(`/applecare/links/${l.id}/send`, { channel: l.channel || "EMAIL" }), "Link sent")} testId={`ac-link-send-${l.id}`}><Send className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title="Reissue" onClick={() => act(l.id, () => api.post(`/applecare/links/${l.id}/reissue`), "Link reissued")} testId={`ac-link-reissue-${l.id}`}><RefreshCw className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title="Revoke" onClick={() => act(l.id, () => api.post(`/applecare/links/${l.id}/revoke`), "Link revoked")} testId={`ac-link-revoke-${l.id}`}><Ban className="h-3.5 w-3.5" /></IconBtn>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------ Plans ------------------------------ */
function Plans() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const load = useCallback(() => api.get("/applecare/plans", { params: q ? { q } : {} }).then(({ data }) => setRows(data.items)).catch(() => setRows([])), [q]);
  useEffect(() => { load(); }, [load]);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/applecare/plans/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} new, ${data.merged} merged, ${data.review_required.length} need review`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const toggle = async (p) => { try { await api.post(`/applecare/plans/${p.id}/${p.active ? "deactivate" : "activate"}`); load(); } catch { toast.error("Failed"); } };

  if (!rows) return <Skeleton />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search part # or name…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" data-testid="ac-plan-search" />
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="ac-plan-import">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={onImport} />
          </label>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/applecare/plans/export`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm hover:bg-slate-50" data-testid="ac-plan-export"><Download className="h-4 w-4" /> Export</a>
        </div>
      </div>
      <Panel title={`Plan Catalogue (${rows.length})`}>
        {rows.length === 0 ? <Empty text="No plans" /> : (
          <Table head={["Part #", "Description", "Family", "Type", "Price", "Frequency", "Enterprise", "Active"]} testId="ac-plans-table">
            {rows.map((p) => (
              <tr key={p.id} className={`border-t border-slate-100 ${p.dq_flag ? "bg-amber-50" : ""}`} data-testid={`ac-plan-row-${p.id}`}>
                <Td mono>{p.apple_part_number}</Td>
                <Td>{p.description}{p.dq_flag && <span className="ml-1 text-[10px] text-amber-600">⚠ {p.dq_reason}</span>}</Td>
                <Td>{p.product_family}</Td>
                <Td>{p.plan_type?.replace(/_/g, " ")}</Td>
                <Td>{money(p.amount, p.currency)}</Td>
                <Td>{p.payment_frequency}</Td>
                <Td>{p.enterprise ? `${p.enterprise_tier || "?"}${p.enterprise_plus ? " +" : ""}` : "—"}</Td>
                <Td><button onClick={() => toggle(p)} data-testid={`ac-plan-toggle-${p.id}`} className={`text-[11px] px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Active" : "Inactive"}</button></Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------ Agreements ------------------------------ */
function Agreements() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ customer_name: "", apple_agreement_number: "", price_tier: "TIER_2", coverage_level: "BOTH", market: "IN", product_families: "IPHONE,IPAD,MAC", effective_from: "2026-01-01", effective_until: "2027-12-31" });
  const load = useCallback(() => api.get("/applecare/agreements").then(({ data }) => { setRows(data.items); setErr(null); }).catch((e) => { setRows([]); setErr(e.response?.status === 403 ? "Enterprise Agreements are restricted to management." : null); }), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await api.post("/applecare/agreements", { ...form, product_families: form.product_families.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) });
      toast.success("Agreement added"); setCreating(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!rows) return <Skeleton />;
  if (err) return <Panel title="Enterprise Agreements"><Empty text={err} /></Panel>;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setCreating((v) => !v)} data-testid="ac-create-agreement-btn" className="bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" /> Add agreement</Button></div>
      {creating && (
        <Panel title="New enterprise agreement">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ac-agreement-form">
            <L label="Customer name"><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ac-agr-customer" /></L>
            <L label="Apple agreement number"><Input value={form.apple_agreement_number} onChange={(e) => setForm({ ...form, apple_agreement_number: e.target.value })} data-testid="ac-agr-number" /></L>
            <L label="Price tier"><select value={form.price_tier} onChange={(e) => setForm({ ...form, price_tier: e.target.value })} data-testid="ac-agr-tier" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option>TIER_1</option><option>TIER_2</option><option>TIER_3</option></select></L>
            <L label="Coverage level"><select value={form.coverage_level} onChange={(e) => setForm({ ...form, coverage_level: e.target.value })} data-testid="ac-agr-coverage" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option>STANDARD</option><option>PLUS</option><option>BOTH</option></select></L>
            <L label="Product families (comma sep)"><Input value={form.product_families} onChange={(e) => setForm({ ...form, product_families: e.target.value })} data-testid="ac-agr-families" /></L>
            <L label="Market"><Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} data-testid="ac-agr-market" /></L>
          </div>
          <div className="flex justify-end mt-3"><Button onClick={create} data-testid="ac-agr-save" className="bg-emerald-700 hover:bg-emerald-800">Save agreement</Button></div>
        </Panel>
      )}
      <Panel title={`Enterprise Agreements (${rows.length})`}>
        {rows.length === 0 ? <Empty text="No agreements" /> : (
          <Table head={["Customer", "Agreement #", "Tier", "Coverage", "Families", "Market", "Status"]} testId="ac-agreements-table">
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <Td>{a.customer_name}</Td>
                <Td mono>{a.apple_agreement_masked}</Td>
                <Td>{a.price_tier}</Td>
                <Td>{a.coverage_level}</Td>
                <Td>{(a.product_families || []).join(", ")}</Td>
                <Td>{a.market}</Td>
                <Td><StatusBadge value={a.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------ Data Quality ------------------------------ */
function DataQuality() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/applecare/plans/data-quality").then(({ data }) => setRows(data.items)).catch(() => setRows([])); }, []);
  if (!rows) return <Skeleton />;
  return (
    <Panel title={`Data Quality (${rows.length})`}>
      {rows.length === 0 ? <Empty text="No data quality issues — catalogue is clean" /> : (
        <Table head={["Part #", "Issue"]} testId="ac-dq-table">
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <Td mono>{r.part_number || "—"}</Td>
              <Td><span className="text-amber-700 text-xs font-medium">{r.type}</span></Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

/* ------------------------------ Channels & Verification ------------------------------ */
function ChannelsVerify() {
  const [ch, setCh] = useState(null);
  const [v, setV] = useState(null);
  const load = useCallback(() => {
    api.get("/applecare/meta").then(({ data }) => setCh(data.channels)).catch(() => setCh([]));
    api.get("/applecare/verification-config").then(({ data }) => setV(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => { try { await api.patch("/applecare/verification-config", v); toast.success("Verification adapter saved"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } };
  if (!ch || !v) return <Skeleton />;
  const pill = (s) => s === "READY"
    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ready</span>
    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Setup required</span>;
  return (
    <div className="space-y-4" data-testid="ac-channels">
      <Panel title="Communication channels">
        <div className="space-y-2 text-sm" data-testid="ac-channel-status">
          {ch.map((c) => (
            <div key={c.value} className="flex items-center gap-3">
              <span className="w-28 font-medium">{c.label}</span>{pill(c.status)}
              {c.status !== "READY" && <span className="text-[11px] text-slate-400">
                {c.value === "SMS" ? "Add TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_FROM_NUMBER to .env"
                  : "Add META_WA_PHONE_NUMBER_ID / META_WA_ACCESS_TOKEN to .env"}</span>}
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Apple device verification adapter">
        <p className="text-[12px] text-slate-500 mb-3">Internal ONE data + manual review is the default. Connect an authorised Apple/partner API when available — no consumer-site scraping.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl" data-testid="ac-verify-config">
          <L label="Provider">
            <select value={v.provider} onChange={(e) => setV({ ...v, provider: e.target.value })} data-testid="ac-verify-provider" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="internal">Internal data + manual review (default)</option>
              <option value="apple_partner_api">Authorised Apple/partner API</option>
            </select>
          </L>
          <L label="Enabled"><label className="flex items-center gap-2 h-9 text-sm"><input type="checkbox" checked={!!v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} data-testid="ac-verify-enabled" /> Use external provider</label></L>
          <L label="Endpoint URL"><Input value={v.endpoint || ""} onChange={(e) => setV({ ...v, endpoint: e.target.value })} placeholder="https://…/verify" data-testid="ac-verify-endpoint" /></L>
          <L label="API key env var name"><Input value={v.api_key_env || ""} onChange={(e) => setV({ ...v, api_key_env: e.target.value })} placeholder="APPLE_VERIFY_KEY" data-testid="ac-verify-keyenv" />{v.api_key_env && <div className={`text-[11px] mt-0.5 ${v.api_key_present ? "text-emerald-600" : "text-amber-600"}`}>{v.api_key_present ? "Key present in .env" : "Key not found in .env"}</div>}</L>
        </div>
        <Button onClick={save} data-testid="ac-verify-save" className="mt-3 bg-emerald-700 hover:bg-emerald-800">Save adapter</Button>
      </Panel>
    </div>
  );
}

/* ------------------------------ Upsell Opportunities ------------------------------ */
const UPSELL_VIEWS = ["ALL", "EMAIL_SENT", "CUSTOMER_VIEWED", "INTERESTED", "PLAN_SELECTED", "CONVERTED", "DECLINED", "NOT_ELIGIBLE"];
function Upsell() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("ALL");
  const load = useCallback(() => {
    api.get("/applecare/upsell/overview").then(({ data }) => setOv(data)).catch(() => {});
    api.get("/applecare/upsell/opportunities", { params: view === "ALL" ? {} : { status: view } })
      .then(({ data }) => setRows(data.items)).catch(() => setRows([]));
  }, [view]);
  useEffect(() => { load(); }, [load]);
  const scan = async () => { try { const { data } = await api.post("/applecare/upsell/scan"); toast.success(`Scanned ${data.orders_scanned} orders, ${data.upsells_created} new`); load(); } catch { toast.error("Scan failed"); } };
  const remind = async () => { try { const { data } = await api.post("/applecare/upsell/run-reminders"); toast.success(`${data.sent} reminder(s) prepared`); load(); } catch { toast.error("Failed"); } };
  if (!ov || !rows) return <Skeleton />;
  const tiles = [
    ["Eligible", ov.eligible_opportunities], ["Emails sent", ov.emails_sent],
    ["Interested", ov.interested], ["Converted", ov.converted],
    ["Conversion", `${ov.conversion_rate_pct}%`], ["Expiring", ov.expiring_soon],
  ];
  return (
    <div className="space-y-4" data-testid="ac-upsell">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {tiles.map(([l, v]) => (
          <div key={l} data-testid={`ac-upsell-tile-${l.toLowerCase().replace(/ /g, "-")}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
            <div className="text-xl font-bold text-slate-900">{v ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={view} onChange={(e) => setView(e.target.value)} data-testid="ac-upsell-view" className="h-9 rounded-md border border-slate-200 px-2 text-sm">
          {UPSELL_VIEWS.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={scan} data-testid="ac-upsell-scan"><RefreshCw className="h-4 w-4 mr-1" /> Scan sales</Button>
          <Button variant="outline" onClick={remind} data-testid="ac-upsell-remind"><Send className="h-4 w-4 mr-1" /> Run reminders</Button>
        </div>
      </div>
      <Panel title={`Opportunities (${rows.length})`}>
        {rows.length === 0 ? <Empty text="No upsell opportunities yet — deliver an Apple order without AppleCare, or use Scan sales" /> : (
          <Table head={["Opportunity", "Customer", "Product", "Order", "Eligibility", "Deadline", "Plan", "Status"]} testId="ac-upsell-table">
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`ac-upsell-${u.id}`}>
                <Td mono>{u.upsell_code}</Td>
                <Td>{u.customer_name}<div className="text-[11px] text-slate-400">{u.contact_email}</div></Td>
                <Td>{u.product_name}<div className="text-[11px] text-slate-400">{u.family_label}</div></Td>
                <Td mono>{u.order_code}</Td>
                <Td><StatusBadge value={u.eligibility} /></Td>
                <Td>{u.eligibility_deadline || "—"}</Td>
                <Td>{u.selected_plan ? `${money(u.selected_plan.amount)} ` : "—"}</Td>
                <Td><StatusBadge value={u.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------ Upsell Config ------------------------------ */
const FAMILIES = ["IPHONE", "IPAD", "MAC", "APPLE_WATCH", "HEADPHONES", "APPLE_DISPLAY"];
function UpsellConfig() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/applecare/upsell/config").then(({ data }) => setC(data)).catch(() => {}); }, []);
  const save = async () => { try { await api.patch("/applecare/upsell/config", c); toast.success("Upsell configuration saved"); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } };
  if (!c) return <Skeleton />;
  const toggleFam = (f) => setC({ ...c, eligible_families: c.eligible_families.includes(f) ? c.eligible_families.filter((x) => x !== f) : [...c.eligible_families, f] });
  return (
    <Panel title="AppleCare+ Upsell Configuration">
      <div className="space-y-4 max-w-2xl" data-testid="ac-upsell-config">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.enabled} onChange={(e) => setC({ ...c, enabled: e.target.checked })} data-testid="ac-cfg-enabled" /> Upsell engine enabled</label>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Eligible Apple families</div>
          <div className="flex flex-wrap gap-2">
            {FAMILIES.map((f) => (
              <button key={f} onClick={() => toggleFam(f)} data-testid={`ac-cfg-fam-${f}`} className={`text-xs px-3 h-8 rounded-full border ${c.eligible_families.includes(f) ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-200 text-slate-600"}`}>{f.replace(/_/g, " ")}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Trigger event"><select value={c.trigger_event} onChange={(e) => setC({ ...c, trigger_event: e.target.value })} data-testid="ac-cfg-trigger" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option value="DELIVERED">Delivery confirmed</option><option value="INVOICE_ISSUED">Invoice issued</option></select></L>
          <L label="First email delay"><select value={c.first_email_delay} onChange={(e) => setC({ ...c, first_email_delay: e.target.value })} data-testid="ac-cfg-delay" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option value="SAME_DAY">Same day</option><option value="1_DAY">1 day after</option><option value="2_DAY">2 days after</option></select></L>
          <L label="Eligibility window (days)"><Input type="number" value={c.eligibility_window_days} onChange={(e) => setC({ ...c, eligibility_window_days: Number(e.target.value) })} data-testid="ac-cfg-window" /></L>
          <L label="Reminder days before deadline"><Input type="number" value={c.reminder_days_before_deadline} onChange={(e) => setC({ ...c, reminder_days_before_deadline: Number(e.target.value) })} data-testid="ac-cfg-reminder-days" /></L>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.reminder_enabled} onChange={(e) => setC({ ...c, reminder_enabled: e.target.checked })} data-testid="ac-cfg-reminder" /> Send one reminder before eligibility closes</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.email_mode === "auto"} onChange={(e) => setC({ ...c, email_mode: e.target.checked ? "auto" : "review" })} data-testid="ac-cfg-automode" /> Auto-send emails (otherwise prepared in Communications for review)</label>
        <Button onClick={save} data-testid="ac-cfg-save" className="bg-emerald-700 hover:bg-emerald-800">Save configuration</Button>
      </div>
    </Panel>
  );
}

/* ------------------------------ primitives ------------------------------ */
const Panel = ({ title, children }) => (
  <section className="border border-slate-200 rounded-lg bg-white p-4">
    <h2 className="text-[13px] font-semibold text-slate-800 mb-3">{title}</h2>{children}
  </section>
);
const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>
    <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">{head.map((h) => <th key={h} className="pb-2 pr-3 font-medium">{h}</th>)}</tr></thead>
    <tbody>{children}</tbody>
  </table></div>
);
const Td = ({ children, mono }) => <td className={`py-2 pr-3 align-top ${mono ? "font-mono text-[12px]" : ""}`}>{children}</td>;
const L = ({ label, children }) => <div><label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</label>{children}</div>;
const IconBtn = ({ children, onClick, title, testId }) => (
  <button onClick={onClick} title={title} data-testid={testId} className="h-7 w-7 grid place-items-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800">{children}</button>
);
const Empty = ({ text }) => <div className="text-[13px] text-slate-400 py-6 text-center">{text}</div>;
const Skeleton = () => <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded animate-pulse" />)}</div>;
